/**
 * The click behind "View project as customer": mint a portal link and open it.
 *
 * A hook rather than a component because there is nothing to render - the menu
 * entry already exists, and what it is missing is the request, the new tab, the
 * busy flag and the one sentence that has to be said when the link leads
 * nowhere.
 *
 * ── WHY THE TAB IS OPENED BEFORE THE REQUEST ───────────────────────────────
 *
 * `window.open` is only allowed while the browser still considers itself inside
 * the click that caused it. Awaiting the mint first breaks that: by the time the
 * URL is known the gesture has expired and the popup blocker eats the tab, on
 * every browser, silently. So a blank tab is opened synchronously and its
 * `location` is set when the answer arrives - the standard shape for "open a
 * link the server has to compute".
 *
 * ⚠ NO `noopener` IN THE FEATURE STRING. `window.open` with `noopener` returns
 * NULL by specification - severing the opener relationship is exactly what makes
 * a window handle impossible to hand back - so the tab opens and the caller is
 * told it did not. This code believed that and ran its "the blocker won"
 * fallback every single time, which navigated the CRM tab as well: one empty tab
 * plus a page that left from under the operator. The opener is severed one line
 * later instead, on the handle we now hold, which gives both halves.
 *
 * The blank tab is CLOSED again if the mint fails. Leaving it would strand the
 * operator on an empty page with no explanation, which reads worse than nothing
 * having happened at all.
 *
 * ── WHY THE REACH IS A TOAST AND NOT A DIALOG ──────────────────────────────
 *
 * The operator asked to see the page, and they are about to. Putting a dialog in
 * front of that would make them dismiss a box to get what they clicked for, and
 * a box that is always dismissed stops being read. The one fact worth
 * interrupting for - "no customer can actually open this" - is worth exactly one
 * sentence, which is what a toast is.
 */
import { useState } from "react";
import { useT } from "../../../i18n/I18nProvider";
import { createPortalPreview } from "../../../lib/portalPreview";
import { useToast } from "../../ui";

export function useClientPreview(): { busy: boolean; open: (projectId: string) => Promise<void> } {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  return {
    busy,
    open: async (projectId) => {
      if (busy) return;
      // Synchronously, inside the click, and WITHOUT `noopener` - see the note at
      // the top for why that flag would break this entirely.
      const tab = window.open("", "_blank");
      setBusy(true);
      try {
        const preview = await createPortalPreview(projectId);
        if (tab) {
          // Severed here rather than through the feature string, so the new tab
          // cannot reach back into the CRM through `window.opener` while we keep
          // the handle we need to steer it. Before the navigation, so the portal
          // never loads with an opener at all.
          tab.opener = null;
          tab.location.href = preview.url;
        } else {
          // A genuine block. Rare, because the open above happens inside the
          // click - but if it happens, SAY SO rather than navigating this tab to
          // the portal: taking the operator out of the CRM is a bigger surprise
          // than a click that reports it did not work, and the browser shows its
          // own blocked-popup control they can allow it from.
          toast(t("projects.clientView.popupBlocked"));
        }
        // After the tab is on its way, so the sentence does not delay the thing
        // the operator actually asked for. Only when the tab actually opened:
        // the toast is a single slot, so on a blocked popup this would replace
        // the one message the operator needs with a note about a page they
        // cannot see.
        if (tab && !preview.reach.visible) {
          toast(t(preview.reach.archived ? "projects.clientView.blockedArchived" : "projects.clientView.blockedNobody"));
        }
      } catch {
        tab?.close();
        toast(t("common.error"));
      } finally {
        setBusy(false);
      }
    },
  };
}
